/**
 * RUN vs CHANGE — the investment class of work (#435, surface 1).
 *
 * A second axis on top of the board's delivery shape (project vs continuous):
 * is a card BUILDING something (an investment — Change) or KEEPING the service
 * running (business-as-usual — Run)? It answers the portfolio question "how much
 * of our effort is building versus keeping the lights on?" without anyone typing
 * a classification on every card.
 *
 * DERIVED, THEN OVERRIDABLE — the same shape as a phase's status (`shownStatusOf`,
 * portfolio.ts) and a card's area:
 *   - The class is DERIVED from the card's TYPE, through its Means:
 *       building            → Change   (a feature, an improvement — investment)
 *       fixing + upkeep     → Run       (an ad-hoc fix, maintenance — BAU)
 *   - A person may OVERRIDE it per card. The override is stored on the card
 *     (`issue.investmentClass`) ONLY when it differs from the derived value;
 *     choosing the derived value again clears it, so a later type change still
 *     flows through untouched cards. `investmentClassOf` reads the effective class.
 *
 * `upkeep` maps to Run deliberately: keeping something running that is neither new
 * nor broken is business-as-usual, not investment. (Settled with Vinnie, #435.)
 */
import type { InvestmentClass, Issue } from './types';
import { typeOf, type IssueType } from './issue-types';
import type { LifecycleTier } from './lifecycle';

export type { InvestmentClass };

/** The board's fallback class for a card whose TYPE gives no signal — an unknown or generic
 *  type (Q3, F7). A project still IN DELIVERY is building, so its untyped work is Change; a
 *  continuous service, a board already in service, or a bare pipeline board is keeping the
 *  lights on, so its untyped work is Run. This is what stops an untyped delivery board from
 *  reading a false "100% Run"; the portfolio and the board's own Metrics strip compute the
 *  tier the same way, so the two agree. */
export function investmentLifecycleDefault(tier: LifecycleTier | null): InvestmentClass {
  return tier === 'indelivery' ? 'change' : 'run';
}

export const INVESTMENT_CLASSES: InvestmentClass[] = ['change', 'run'];

/** The word shown on a chip or in the picker. */
export const INVESTMENT_LABEL: Record<InvestmentClass, string> = {
  change: 'Change',
  run: 'Run',
};

/** The one-line gloss shown where somebody is choosing, not reading a chart. */
export const INVESTMENT_BLURB: Record<InvestmentClass, string> = {
  change: 'Building something new, or a real improvement. Counts as investment.',
  run: 'Keeping the service running — ad-hoc fixes and upkeep. Counts as business-as-usual (BAU).',
};

/** The tone-class modifier for the R3 badge / swatch (styles.css defines the tint).
 *  Change = brand petrol tint, Run = steel-grey — kept clear of the RAG health inks
 *  and the ledger-teal money hue. */
export const INVESTMENT_TONE: Record<InvestmentClass, string> = {
  change: 'is-change',
  run: 'is-run',
};

/** The class a card's TYPE implies, before any override (Q3 precedence: override → type →
 *  lifecycle). A KNOWN type decides it: building → Change, every other Means (fixing, upkeep)
 *  → Run. An UNKNOWN or generic type gives NO signal, so it falls to `lifecycleDefault` — the
 *  board's lifecycle (In-Delivery → Change, else Run) — instead of a blanket Run that read a
 *  whole untyped delivery board as "100% Run" (F7). Defaults to Run when no lifecycle is
 *  passed, so a caller that cannot know the board's tier still never inflates Change. */
export function derivedInvestmentClass(
  kind: string,
  types: IssueType[],
  lifecycleDefault: InvestmentClass = 'run',
): InvestmentClass {
  const t = typeOf(kind, types);
  if (!t) return lifecycleDefault; // unknown/generic type — no signal; the board's lifecycle decides
  return t.means === 'building' ? 'change' : 'run';
}

/** The EFFECTIVE class of a card: the manual override when one is set, else the
 *  type-derived class (which, for an untyped card, follows the board's lifecycle). The single
 *  reader everything else should use. */
export function investmentClassOf(
  kind: string,
  types: IssueType[],
  override?: InvestmentClass | null,
  lifecycleDefault: InvestmentClass = 'run',
): InvestmentClass {
  return override ?? derivedInvestmentClass(kind, types, lifecycleDefault);
}

/**
 * WHAT TO STORE when a person picks `picked` on a card whose type derives `derived`.
 * The override is kept ONLY when it differs — picking the derived value (or
 * "reset to auto") returns `null`, so the card goes back to following its type and
 * a later type change flows through it untouched. Mirrors `shownStatusOf`.
 */
export function investmentOverrideToStore(
  picked: InvestmentClass,
  derived: InvestmentClass,
): InvestmentClass | null {
  return picked === derived ? null : picked;
}

export interface InvestmentMix {
  change: number;
  run: number;
  total: number;
  /** Change as a whole-number percentage of the total; `run%` is `100 - changePct`
   *  so the two always sum to 100 and the bar never overflows. */
  changePct: number;
  runPct: number;
  /** The whole-board label: Change / Run / Mixed, or null when there are no cards
   *  to classify (the chip is then hidden and the tile invites adding cards). */
  label: 'Change' | 'Run' | 'Mixed' | null;
}

/** The mix from a pair of counts — the one place the split %, the 100-sum and the
 *  Change/Run/Mixed/null label are decided, so a board and the whole portfolio read
 *  the same way. */
function mixFromCounts(change: number, run: number): InvestmentMix {
  const total = change + run;
  const changePct = total === 0 ? 0 : Math.round((change / total) * 100);
  const label: InvestmentMix['label'] =
    total === 0 ? null : change > 0 && run > 0 ? 'Mixed' : change > 0 ? 'Change' : 'Run';
  return { change, run, total, changePct, runPct: total === 0 ? 0 : 100 - changePct, label };
}

/**
 * The board's work mix, BY CARD COUNT (the settled basis, #435) — every card's
 * effective class rolled up. Both classes present ⇒ "Mixed" (the default framing);
 * one only ⇒ that class; none ⇒ null.
 */
export function boardInvestmentMix(
  issues: Issue[],
  types: IssueType[],
  lifecycleDefault: InvestmentClass = 'run',
): InvestmentMix {
  let change = 0;
  let run = 0;
  for (const i of issues) {
    if (investmentClassOf(i.kind, types, i.investmentClass, lifecycleDefault) === 'change') change += 1;
    else run += 1;
  }
  return mixFromCounts(change, run);
}

/**
 * The whole-portfolio mix — several boards' mixes summed into one Change/Run split.
 * Each part was derived with its OWN board's types, so the portfolio only adds the
 * card counts and re-decides the split; a board with no cards contributes nothing.
 * The band at the top of the Portfolio and page 1 of the steering pack read this.
 */
export function combineInvestmentMix(parts: InvestmentMix[]): InvestmentMix {
  let change = 0;
  let run = 0;
  for (const p of parts) {
    change += p.change;
    run += p.run;
  }
  return mixFromCounts(change, run);
}
